import { useFormContext } from 'react-hook-form';
import { Suspense } from 'react';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { FormCheckboxGroupField, lt, useFeatureFlag } from '@mews/framework';
import { Service } from '@mews-commander/data-access';
import { compactArray } from '@mews/fp';
import { Loader } from '@mews-commander/ui';
import { useLocalizedTimeUnit } from '../../../../../../services';
import {
    AdditionalServiceOptionsNames,
    CustomItemsOptionsNames,
    OptionsNames,
    ProductsOptionsNames,
} from '../../../../../schemas';
import { RoutingRulesFormFields } from '../../../utils';
import { ProductsSelectField } from './ProductsSelectField';
import { CustomItemsSelectField } from './CustomItemsSelectField';

export const ItemsServiceOptionsField = ({
    service,
    isBookableService,
}: {
    service: Service;
    isBookableService: boolean;
}) => {
    const { watch, setValue, trigger } = useFormContext();
    const localizedTimeUnitsName = useLocalizedTimeUnit(service.id, { form: 'plural' });
    const newRoutingRulesPOSEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesPOSEnabled);

    const productsOption = isBookableService ? OptionsNames.Products : AdditionalServiceOptionsNames.Products;
    const serviceOptionsFieldName = isBookableService
        ? RoutingRulesFormFields.serviceOptions(service.id)
        : RoutingRulesFormFields.additionalServiceOptions(service.id);
    const serviceOptions: (OptionsNames | AdditionalServiceOptionsNames)[] = watch(serviceOptionsFieldName) || [];
    const serviceOptionsWithoutProducts = [
        {
            name: serviceOptionsFieldName,
            value: OptionsNames.TimeUnit,
            label: localizedTimeUnitsName,
        },
        {
            name: serviceOptionsFieldName,
            value: OptionsNames.CityTaxes,
            label: lt(TextKey.RoutingRulesFormCityTaxesOption),
        },
        {
            name: serviceOptionsFieldName,
            value: OptionsNames.Deposits,
            label: lt(TextKey.RoutingRulesFormDepositsOption),
        },
        {
            name: serviceOptionsFieldName,
            value: OptionsNames.AdditionalExpenses,
            label: lt(TextKey.RoutingRulesFormAdditionalExpensesOption),
        },
    ];

    const areProductsEnabled = serviceOptions.includes(productsOption);
    const areCustomItemsEnabled =
        newRoutingRulesPOSEnabled && serviceOptions.includes(AdditionalServiceOptionsNames.CustomItems);

    return (
        <FormCheckboxGroupField
            required
            onChange={(_, value) => {
                if (!value.includes(productsOption)) {
                    if (isBookableService) {
                        setValue(RoutingRulesFormFields.serviceProductsType(service.id), ProductsOptionsNames.All);
                    } else {
                        setValue(
                            RoutingRulesFormFields.additionalServiceProductsType(service.id),
                            ProductsOptionsNames.All
                        );
                    }
                    void trigger();
                }
                if (!isBookableService && !value.includes(AdditionalServiceOptionsNames.CustomItems)) {
                    setValue(RoutingRulesFormFields.customItemsType(service.id), CustomItemsOptionsNames.All);
                    void trigger();
                }
            }}
            options={compactArray([
                ...(isBookableService ? serviceOptionsWithoutProducts : []),
                {
                    name: serviceOptionsFieldName,
                    value: productsOption,
                    label: (
                        <>
                            {lt(TextKey.RoutingRulesFormProductsOption)}
                            {areProductsEnabled && (
                                <Suspense fallback={<Loader />}>
                                    <ProductsSelectField
                                        key={service.id}
                                        service={service}
                                        isBookableService={isBookableService}
                                    />
                                </Suspense>
                            )}
                        </>
                    ),
                },
                newRoutingRulesPOSEnabled && !isBookableService
                    ? {
                          name: serviceOptionsFieldName,
                          value: AdditionalServiceOptionsNames.CustomItems,
                          label: (
                              <>
                                  {lt(TextKey.RoutingRulesFormCustomItemsOption)}
                                  {areCustomItemsEnabled && (
                                      <Suspense fallback={<Loader />}>
                                          <CustomItemsSelectField key={service.id} service={service} />
                                      </Suspense>
                                  )}
                              </>
                          ),
                      }
                    : null,
            ])}
            name={serviceOptionsFieldName}
        />
    );
};
